import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, CheckCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Flashs = () => {
  const [flashsData, setFlashsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("default");
  const [filterSize, setFilterSize] = useState("all");
  const [filterAvailable, setFilterAvailable] = useState("all");
  
  const [selectedFlash, setSelectedFlash] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reservationLoading, setReservationLoading] = useState(false);
  const [reservationSuccess, setReservationSuccess] = useState(false);
  const [reservationForm, setReservationForm] = useState({
    nom: "",
    email: "",
    instagram: "",
    preference_contact: "instagram",
    message: "",
  });

  // Fetch flashs from API
  useEffect(() => {
    const fetchFlashs = async () => {
      try {
        const response = await axios.get(`${API}/flashs`);
        setFlashsData(response.data);
      } catch (error) {
        console.error("Error fetching flashs:", error);
        toast.error("Erreur lors du chargement des flashs");
      } finally {
        setLoading(false);
      }
    };
    fetchFlashs();
  }, []);

  const handleReservation = async (e) => {
    e.preventDefault();
    setReservationLoading(true);

    try {
      await axios.post(`${API}/flash-reservations`, {
        ...reservationForm,
        flash_id: selectedFlash.id,
        flash_titre: selectedFlash.titre,
        flash_prix: selectedFlash.prix,
        flash_taille_min: selectedFlash.taille_min,
      });
      setReservationSuccess(true);
      toast.success("Demande de réservation envoyée !");
    } catch (error) {
      console.error(error);
      if (error.response?.status === 400) {
        toast.error("Ce flash n'est plus disponible.");
      } else {
        toast.error("Une erreur est survenue.");
      }
    } finally {
      setReservationLoading(false);
    }
  };

  const openReservationDialog = (flash) => {
    setSelectedFlash(flash);
    setReservationSuccess(false);
    setReservationForm({ nom: "", email: "", instagram: "", preference_contact: "instagram", message: "" });
    setDialogOpen(true);
  };

  // Get size category
  const getSizeCategory = (taille_min) => {
    if (taille_min < 15) return "petit";
    if (taille_min < 30) return "moyen";
    return "grand";
  };

  // Filter and sort
  let filteredFlashs = [...flashsData];

  if (filterSize !== "all") {
    filteredFlashs = filteredFlashs.filter((f) => getSizeCategory(f.taille_min) === filterSize);
  }

  if (filterAvailable === "available") {
    filteredFlashs = filteredFlashs.filter((f) => f.disponible);
  } else if (filterAvailable === "reserved") {
    filteredFlashs = filteredFlashs.filter((f) => !f.disponible);
  }

  if (sortBy === "price-asc") {
    filteredFlashs.sort((a, b) => a.prix - b.prix);
  } else if (sortBy === "price-desc") {
    filteredFlashs.sort((a, b) => b.prix - a.prix);
  }

  return (
    <div data-testid="flashs-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            Flashs
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            Designs disponibles
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            Des créations prêtes à être tatouées. Les prix affichés sont à titre indicatif et peuvent varier en fonction de la taille et de l'emplacement souhaité.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8">
        <div className="page-container">
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Par défaut</SelectItem>
                <SelectItem value="price-asc">Prix croissant</SelectItem>
                <SelectItem value="price-desc">Prix décroissant</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterSize} onValueChange={setFilterSize}>
              <SelectTrigger className="w-[180px]" data-testid="size-filter">
                <SelectValue placeholder="Taille" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes tailles</SelectItem>
                <SelectItem value="petit">Petit (2-15 cm)</SelectItem>
                <SelectItem value="moyen">Moyen (15-30 cm)</SelectItem>
                <SelectItem value="grand">Grand (30 cm+)</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterAvailable} onValueChange={setFilterAvailable}>
              <SelectTrigger className="w-[180px]" data-testid="availability-filter">
                <SelectValue placeholder="Disponibilité" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="available">Disponibles</SelectItem>
                <SelectItem value="reserved">Réservés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFlashs.map((flash) => (
              <div
                key={flash.id}
                className="group relative border border-border/40 bg-card overflow-hidden card-hover"
                data-testid={`flash-card-${flash.id}`}
              >
                {/* Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={flash.image_url}
                    alt={flash.titre}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    {flash.disponible ? (
                      <span className="badge-status badge-available">
                        Disponible
                      </span>
                    ) : (
                      <span className="badge-status badge-reserved">
                        Réservé
                      </span>
                    )}
                  </div>
                  {flash.unique && flash.disponible && (
                    <div className="absolute top-4 left-4">
                      <span className="badge-status bg-primary text-primary-foreground">
                        Unique
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-heading text-xl mb-1">{flash.titre}</h3>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Min. {flash.taille_min} cm
                      </p>
                    </div>
                    <p className="price-display">{flash.prix}€</p>
                  </div>

                  {flash.disponible ? (
                    <Button
                      onClick={() => openReservationDialog(flash)}
                      className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full"
                      data-testid={`reserve-flash-${flash.id}`}
                    >
                      Réserver ce flash
                    </Button>
                  ) : (
                    <Button disabled className="w-full opacity-50 h-12">
                      Déjà réservé
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredFlashs.length === 0 && (
            <div className="text-center py-16">
              <p className="text-muted-foreground">Aucun flash ne correspond à vos critères.</p>
            </div>
          )}
        </div>
      </section>

      {/* Reservation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          {reservationSuccess ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" strokeWidth={1} />
              <DialogTitle className="text-xl font-heading mb-2">
                Demande envoyée !
              </DialogTitle>
              <DialogDescription className="mb-6">
                Je vous recontacterai rapidement pour confirmer la réservation et organiser la séance.
              </DialogDescription>
              <Button onClick={() => setDialogOpen(false)} variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
                Fermer
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  Réserver "{selectedFlash?.titre}"
                </DialogTitle>
                <DialogDescription>
                  Remplissez ce formulaire pour demander la réservation de ce flash. Je vous recontacterai pour confirmer.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleReservation} className="space-y-4 mt-4">
                <div className="form-group">
                  <Label htmlFor="res-nom" className="form-label">Nom / Prénom *</Label>
                  <Input
                    id="res-nom"
                    value={reservationForm.nom}
                    onChange={(e) => setReservationForm({ ...reservationForm, nom: e.target.value })}
                    required
                    className="form-input"
                    data-testid="reservation-nom"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="res-email" className="form-label">Email *</Label>
                  <Input
                    id="res-email"
                    type="email"
                    value={reservationForm.email}
                    onChange={(e) => setReservationForm({ ...reservationForm, email: e.target.value })}
                    required
                    className="form-input"
                    data-testid="reservation-email"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="res-instagram" className="form-label">Instagram *</Label>
                  <Input
                    id="res-instagram"
                    value={reservationForm.instagram}
                    onChange={(e) => setReservationForm({ ...reservationForm, instagram: e.target.value })}
                    required
                    placeholder="@votre_pseudo"
                    className="form-input"
                    data-testid="reservation-instagram"
                  />
                </div>

                <div className="form-group">
                  <Label className="form-label">Préférence de contact *</Label>
                  <RadioGroup
                    value={reservationForm.preference_contact}
                    onValueChange={(v) => setReservationForm({ ...reservationForm, preference_contact: v })}
                    className="flex gap-6 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="instagram" id="res-contact-instagram" />
                      <Label htmlFor="res-contact-instagram" className="cursor-pointer">Instagram</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="email" id="res-contact-email" />
                      <Label htmlFor="res-contact-email" className="cursor-pointer">Email</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="form-group">
                  <Label htmlFor="res-message" className="form-label">Message</Label>
                  <Textarea
                    id="res-message"
                    value={reservationForm.message}
                    onChange={(e) => setReservationForm({ ...reservationForm, message: e.target.value })}
                    placeholder="Zone et taille souhaitées, questions, ..."
                    className="form-textarea"
                    data-testid="reservation-message"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="flex-1 border-foreground text-foreground hover:bg-foreground hover:text-background"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 flex-1"
                    disabled={reservationLoading}
                    data-testid="submit-reservation"
                  >
                    {reservationLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Envoyer"
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Flashs;
